import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import {
    Button,
    FormField,
    Header,
    Island,
    OverlayContent,
    Radiobox,
    TextInputCompact,
    VStack,
} from '@direct-frontend/components';
import { Form, useField, useFormInstance, useFormState } from '@adv-frontend/form-kit';
import { useFormFieldErrorProps } from '#shared/lib/form';
import { Modal } from '#shared/ui/Modal';
import {
    GSC_CONNECTION_METHOD,
    gscConnectionForm,
    gscConnectionFormGetErrorMessage,
    type GscConnectionFormValues,
    type GscConnectionMethod,
} from '../../model';

interface GscConnectionModalProps {
    initialMethod: GscConnectionMethod;
    isOpen: boolean;
    onAutomaticConnect: () => boolean;
    onClose: VoidFunction;
    onVerificationCodeSubmit: (verificationCode: string) => Promise<boolean>;
    verificationCode: string;
}

const GscConnectionFormContent = ({
    formId,
    onAutomaticConnect,
    onClose,
}: {
    formId: string;
    onAutomaticConnect: () => boolean;
    onClose: VoidFunction;
}) => {
    const { fields, submit } = useFormInstance({ form: gscConnectionForm, id: formId });
    const { isSubmitting } = useFormState({ form: gscConnectionForm, id: formId });
    const methodField = useField(fields.method);
    const verificationCodeField = useField(fields.verificationCode);
    const verificationCodeErrorProps = useFormFieldErrorProps(
        verificationCodeField.state,
        gscConnectionFormGetErrorMessage
    );
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleSubmit = useCallback(async () => {
        if (isSubmitting) {
            return;
        }

        if (methodField.value === GSC_CONNECTION_METHOD.AUTOMATIC) {
            // OAuth popup должен открыться синхронно в рамках пользовательского клика.
            if (onAutomaticConnect()) {
                onClose();
            }

            return;
        }

        const isSaved = await submit();

        if (isSaved && isMountedRef.current) {
            onClose();
        }
    }, [isSubmitting, methodField.value, onAutomaticConnect, onClose, submit]);

    return (
        <Modal isOpen onClose={onClose} data-testid="GscConnectionModal">
            <OverlayContent
                bottomButtons={
                    <>
                        <Button color="accent" isLoading={isSubmitting} onClick={handleSubmit} size="m">
                            Сохранить
                        </Button>
                        <Button color="normal" onClick={onClose} size="m">
                            Отмена
                        </Button>
                    </>
                }
            >
                <Island width="536px">
                    <VStack gap="28">
                        <Header level="h2">Подключение Google Search Console</Header>
                        <Radiobox
                            {...methodField.getProps()}
                            aria-label="Способ подключения"
                            data-testid="GscConnectionModal.Method"
                            options={[
                                {
                                    value: GSC_CONNECTION_METHOD.AUTOMATIC,
                                    content: 'Автоматически через авторизацию в аккаунте',
                                },
                                {
                                    value: GSC_CONNECTION_METHOD.VERIFICATION_CODE,
                                    content: 'По коду верификации',
                                },
                            ]}
                        />
                        {methodField.value === GSC_CONNECTION_METHOD.VERIFICATION_CODE ? (
                            <FormField
                                {...verificationCodeErrorProps}
                                data-testid="GscConnectionModal.VerificationCodeField"
                            >
                                {(controlProps) => (
                                    <TextInputCompact
                                        {...controlProps}
                                        {...verificationCodeField.getProps()}
                                        aria-label="Код верификации"
                                        data-testid="GscConnectionModal.VerificationCodeInput"
                                        label="Код верификации"
                                        placeholder="Укажите код"
                                        size="m"
                                    />
                                )}
                            </FormField>
                        ) : null}
                    </VStack>
                </Island>
            </OverlayContent>
        </Modal>
    );
};

export const GscConnectionModal = ({
    initialMethod,
    isOpen,
    onAutomaticConnect,
    onClose,
    onVerificationCodeSubmit,
    verificationCode,
}: GscConnectionModalProps) => {
    const formId = useId();
    const context = useMemo(() => {
        return { onVerificationCodeSubmit };
    }, [onVerificationCodeSubmit]);
    const initialValue = useMemo<GscConnectionFormValues>(() => {
        return { method: initialMethod, verificationCode };
    }, [initialMethod, verificationCode]);

    if (!isOpen) {
        return null;
    }

    return (
        <Form form={gscConnectionForm} id={formId} context={context} initialValue={initialValue}>
            <GscConnectionFormContent formId={formId} onAutomaticConnect={onAutomaticConnect} onClose={onClose} />
        </Form>
    );
};
